import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [loadMoreContainer] - The manual Load more control container.
 * @property {HTMLButtonElement} [loadMoreButton] - The manual Load more button.
 * @property {HTMLParagraphElement} [loadMoreProgress] - The count of eligible cards displayed and available.
 * @property {HTMLElement} [restoreContainer] - The grid wrapper masked while a directly linked page is restored.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isLoadingNextPage = false;

  /** @type {number | undefined} */
  #eligibleResultsCount;

  /** @type {number} */
  #resultsVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    const { restoreContainer } = this.refs;
    if (restoreContainer?.classList.contains('main-collection-grid--restoring')) {
      requestAnimationFrame(() => {
        restoreContainer.classList.add('main-collection-grid--restore-visible');
      });
    }

    void this.#restoreInitialPages();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Appends the next Shopify page when the visitor explicitly requests it.
   *
   * @param {Event} event
   */
  async loadNextPage(event) {
    event.preventDefault();

    if (this.#isLoadingNextPage) return;

    this.#isLoadingNextPage = true;
    this.#setLoadMoreButtonState(true);

    try {
      await this.#renderNextPage();
    } finally {
      this.#isLoadingNextPage = false;
      this.#setLoadMoreButtonState(false);
      this.#syncLoadMoreButton();
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * When an infinite-scroll URL is loaded directly, Shopify renders only the
   * requested page. Restore every earlier page so the list has the same shape
   * it had when the visitor reached that URL by scrolling.
   */
  async #restoreInitialPages() {
    const { grid, cards } = this.refs;
    const initialPageCard = Array.isArray(cards) ? cards[0] : undefined;
    const initialPage = Number(initialPageCard?.dataset.page);

    if (grid && initialPageCard && Number.isInteger(initialPage) && initialPage > 1) {
      const restoreStartedAt = performance.now();

      try {
        for (let page = initialPage - 1; page >= 1; page--) {
          await this.#fetchSpecificPage(page);

          const pageItemElements = this.#getGridForPage(page);
          if (pageItemElements) {
            grid.prepend(...pageItemElements);
          }
        }

        this.#aspectRatioHelper?.processNewElements();
        initialPageCard.scrollIntoView({ behavior: 'instant', block: 'start' });
        await new Promise((resolve) => requestAnimationFrame(resolve));
      } finally {
        const minimumRestoreDuration = 500;
        const remainingDuration = minimumRestoreDuration - (performance.now() - restoreStartedAt);

        if (remainingDuration > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingDuration));
        }

        this.#finishInitialPageRestore();
      }
    } else {
      this.#finishInitialPageRestore();
    }

    if (this.loadMoreEnabled) {
      this.#syncLoadMoreButton();
      void this.#countRemainingResults();
      return;
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
  }

  /**
   * Reveals a directly linked collection page only after its earlier pages
   * have been restored and the viewport has been moved into place.
   */
  #finishInitialPageRestore() {
    document.documentElement.classList.remove('collection-page-restoring');

    const { restoreContainer } = this.refs;
    if (!restoreContainer) return;

    restoreContainer.classList.remove('main-collection-grid--restoring', 'main-collection-grid--restore-visible');
    restoreContainer.setAttribute('aria-busy', 'false');
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    if (!this.loadMoreEnabled) {
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Whether this list uses an explicit button rather than an intersection observer.
   *
   * @returns {boolean}
   */
  get loadMoreEnabled() {
    return this.getAttribute('load-more') === 'true';
  }

  /**
   * Shows the Load more control only while another Shopify page is available.
   */
  #syncLoadMoreButton() {
    if (!this.loadMoreEnabled) return;

    const { grid, loadMoreButton, loadMoreContainer, loadMoreProgress } = this.refs;
    if (!grid || !loadMoreContainer) return;

    const lastPage = Number(grid.dataset.lastPage);
    const lastCard = grid.querySelector(':scope > [ref="cards[]"]:last-of-type');
    const currentPage = Number(lastCard?.dataset.page);

    if (Number.isInteger(lastPage) && Number.isInteger(currentPage)) {
      const renderedCardsCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
      const remainingResults =
        this.#eligibleResultsCount === undefined ? undefined : Math.max(this.#eligibleResultsCount - renderedCardsCount, 0);
      const hasNextPage = currentPage < lastPage && remainingResults !== 0;

      if (loadMoreButton) loadMoreButton.hidden = !hasNextPage;

      if (loadMoreProgress) {
        if (this.#eligibleResultsCount !== undefined) {
          loadMoreProgress.textContent =
            loadMoreProgress.dataset.template
              ?.replace('__current__', String(renderedCardsCount))
              .replace('__total__', String(this.#eligibleResultsCount)) ?? '';
          loadMoreProgress.hidden = false;
        } else {
          loadMoreProgress.hidden = true;
        }
      }
    }
  }

  /**
   * Counts cards after the current Shopify source page. The collection section
   * already removes locally ineligible products, so counting its rendered HTML
   * gives customers the same total they can actually load.
   */
  async #countRemainingResults() {
    if (!this.loadMoreEnabled) return;

    const { grid } = this.refs;
    if (!grid) return;

    const lastCard = grid.querySelector(':scope > [ref="cards[]"]:last-of-type');
    const currentPage = Number(lastCard?.dataset.page);
    const lastPage = Number(grid.dataset.lastPage);

    if (!Number.isInteger(currentPage) || !Number.isInteger(lastPage)) return;

    const resultsVersion = this.#resultsVersion;
    let eligibleResultsCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;

    for (let page = currentPage + 1; page <= lastPage; page++) {
      await this.#fetchSpecificPage(page);
      if (resultsVersion !== this.#resultsVersion) return;

      eligibleResultsCount += this.#getGridForPage(page)?.length ?? 0;
    }

    if (resultsVersion !== this.#resultsVersion) return;

    this.#eligibleResultsCount = eligibleResultsCount;
    this.#syncLoadMoreButton();

    // The count should not retain every rendered source page in memory. Keep
    // only the next page, which makes the first button click immediate.
    const latestCard = grid.querySelector(':scope > [ref="cards[]"]:last-of-type');
    const nextPage = Number(latestCard?.dataset.page) + 1;
    for (const page of this.pages.keys()) {
      if (page !== nextPage) this.pages.delete(page);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoadMoreButtonState(isLoading) {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    loadMoreButton.disabled = isLoading;
    loadMoreButton.setAttribute('aria-busy', String(isLoading));
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#resultsVersion += 1;
    this.#eligibleResultsCount = undefined;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage || this.loadMoreEnabled) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        if (this.loadMoreEnabled) {
          this.#syncLoadMoreButton();
          void this.#countRemainingResults();
          return;
        }

        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
